import { AccountType } from "@common/constants/domains/accounting/accounts";
import { Form1099Box } from "@common/constants/domains/accounting/form1099Box";
import { AccountRole } from "@common/types/domains/accounting/account";
import { compareByLeafThenCodeThenName } from "./editorRowSorting";

// Working copy of an account inside the editor. Host-agnostic: it carries the
// on-screen `codeText` (the host owns any numeric `code` off-tree), `role` and
// the template's `form1099Box`, but none of the other template-only columns
// (variableKind / kickOriginalCode). The id is a real
// uuid for existing rows or a "temp:" client id for new ones.
export interface DraftAccount {
    id: string;
    entityId: number;
    codeText: string | null;
    name: string;
    type: AccountType;
    isDisabled: boolean;
    // Data about the row (Source column + source filters), not a capability.
    isManuallyCreated: boolean;
    kickOriginalCode?: number | null;
    // Templates only; undefined on rows the draft ops create, read as no role.
    role?: AccountRole | null;
    // Templates only; undefined on rows the draft ops create, read as no box.
    form1099Box?: Form1099Box | null;
    parentGroupId: string | null;
    // Per-scope ordering position. Drives sibling order (see buildNestedRows)
    // and is serialized into the host's save payload so add/insert ordering
    // survives a save.
    sortOrder: number;
}

export interface DraftGroup {
    id: string;
    entityId: number;
    name: string;
    type: AccountType;
    parentGroupId: string | null;
    sortOrder: number;
}

export interface DraftState {
    accounts: DraftAccount[];
    groups: DraftGroup[];
}

export type EditableAccountPatch = Partial<
    Pick<DraftAccount, "name" | "codeText" | "form1099Box">
>;

// Generates a fresh client temp id; injected so the pure operations stay
// deterministic and unit-testable.
export type GenerateId = () => string;

// Injected code allocation, the same treatment as GenerateId. Create and
// relocate stay distinct so a live-chart host can suggest on create and return
// the moving code unchanged on drag; templates ignore the discriminant and
// always scan the type band.
export type AllocateCodeText = (
    params:
        | {
              kind: "create";
              type: AccountType;
              accounts: DraftAccount[];
              targets: Array<{
                  entityId: number;
                  parentGroupId: string | null;
              }>;
          }
        | {
              kind: "relocate";
              type: AccountType;
              accounts: DraftAccount[];
              currentCodeText: string | null;
          },
) => string | null;

export const seedDraftState = (data: {
    accounts: DraftAccount[];
    groups: DraftGroup[];
}): DraftState => ({
    // Sort once on load, matching the leaf/code/name order untouched charts'
    // sortOrder was backfilled from. Display order is derived from sortOrder
    // (see canonicalAccountOrder/buildNestedRows).
    accounts: [...data.accounts].sort(compareByLeafThenCodeThenName),
    groups: [...data.groups],
});

// Replaces a single account (matched by id) by applying `update`, leaving every
// other account untouched. Shared by the account mutation operations.
export const replaceAccount = (
    accounts: DraftAccount[],
    id: string,
    update: (account: DraftAccount) => DraftAccount,
): DraftAccount[] =>
    accounts.map((account) => (account.id === id ? update(account) : account));
